/**
 * ss-video-slider.js
 * Carga diferida de los videos de la sección "Videos slider" (ss-video-slider.liquid).
 *
 * Objetivo: que la página no descargue un solo byte de video hasta que el item
 * entra en pantalla. Antes se renderizaban N <video autoplay> + pósters de 3840px,
 * lo que disparaba varios MB en el load inicial y penalizaba PSI/Core Web Vitals.
 *
 * Comportamiento:
 *  - IntersectionObserver: crea el <video> y lo reproduce solo al entrar en viewport.
 *  - Pausa al salir de pantalla y cuando la pestaña queda oculta.
 *  - Libera (unload) los videos más antiguos: máximo MAX_LOADED en memoria.
 *  - prefers-reduced-motion o modo ahorro de datos: no autoplay, muestra botón play.
 *  - Sin JS solo se ve el póster (lazy), que es el estado por defecto del markup.
 */
(function () {
  'use strict';

  if (!window.customElements || customElements.get('lazy-video-slider')) return;

  var MAX_LOADED = 6;

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function saveData() {
    var conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!conn) return false;
    return conn.saveData === true || /(^|-)2g$/.test(conn.effectiveType || '');
  }

  class VideoSlider extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.items = Array.prototype.slice.call(this.querySelectorAll('.video-slider-item[data-video-src]'));
      if (!this.items.length) return;

      this.loaded = [];
      this.onVisibility = this.onVisibility.bind(this);

      this.items.forEach(function (item) {
        var button = item.querySelector('.video-slider__play');
        if (button) {
          button.addEventListener('click', function () {
            item.classList.remove('needs-play');
            this.activate(item);
          }.bind(this));
        }
      }, this);

      // Sin autoplay: el usuario pide el video explícitamente.
      if (prefersReducedMotion() || saveData()) {
        this.items.forEach(function (item) {
          item.classList.add('needs-play');
        });
        return;
      }

      if (!('IntersectionObserver' in window)) {
        this.items.forEach(function (item) {
          this.activate(item);
        }, this);
        return;
      }

      this.observer = new IntersectionObserver(this.handleIntersect.bind(this), {
        rootMargin: '150px',
        threshold: 0.2
      });
      this.items.forEach(function (item) {
        this.observer.observe(item);
      }, this);

      document.addEventListener('visibilitychange', this.onVisibility);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      document.removeEventListener('visibilitychange', this.onVisibility);
    }

    handleIntersect(entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) this.activate(entry.target);
        else this.deactivate(entry.target);
      }, this);
    }

    onVisibility() {
      if (document.hidden) {
        this.loaded.forEach(function (item) {
          var video = item.querySelector('video');
          if (video) video.pause();
        });
      } else {
        this.items.forEach(function (item) {
          if (item.vsVisible) this.activate(item);
        }, this);
      }
    }

    activate(item) {
      item.vsVisible = true;
      if (document.hidden) return;

      var video = this.ensureVideo(item);
      if (!video) return;

      var played = video.play();
      if (played && played.catch) {
        played.catch(function () {
          // Autoplay bloqueado por el navegador: dejamos el póster + botón play.
          item.classList.add('needs-play');
        });
      }
    }

    deactivate(item) {
      item.vsVisible = false;
      var video = item.querySelector('video');
      if (video) video.pause();
    }

    ensureVideo(item) {
      var src = item.getAttribute('data-video-src');
      if (!src) return null;

      var video = item.querySelector('video');
      if (video && video.getAttribute('src')) {
        this.touch(item);
        return video;
      }

      if (!video) {
        video = document.createElement('video');
        video.className = 'video-slider__video';
        video.muted = true;
        video.defaultMuted = true;
        video.loop = true;
        video.playsInline = true;
        video.autoplay = true;
        video.controls = false;
        video.tabIndex = -1;
        video.disableRemotePlayback = true;
        // Atributos explícitos: iOS exige muted/playsinline en el markup para autoplay.
        video.setAttribute('muted', '');
        video.setAttribute('playsinline', '');
        video.setAttribute('loop', '');
        video.setAttribute('aria-hidden', 'true');
        video.addEventListener('playing', function () {
          item.classList.add('is-playing');
        });
        item.appendChild(video);
      }

      video.preload = 'auto';
      video.setAttribute('src', src);
      this.touch(item);
      this.trim();
      return video;
    }

    touch(item) {
      var index = this.loaded.indexOf(item);
      if (index !== -1) this.loaded.splice(index, 1);
      this.loaded.push(item);
    }

    // Mantiene como máximo MAX_LOADED videos en memoria (los visibles nunca se liberan).
    trim() {
      for (var i = 0; i < this.loaded.length && this.loaded.length > MAX_LOADED; ) {
        var item = this.loaded[i];
        if (item.vsVisible) {
          i++;
          continue;
        }
        this.unload(item);
        this.loaded.splice(i, 1);
      }
    }

    unload(item) {
      var video = item.querySelector('video');
      if (!video) return;
      video.pause();
      video.removeAttribute('src');
      video.load();
      item.classList.remove('is-playing');
    }
  }

  customElements.define('lazy-video-slider', VideoSlider);
})();
